import React from 'react';
import { ShoppingBag, Eye } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const ProductCard = ({ product, onViewDetails }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div 
      data-testid={`product-card-${product.id}`}
      className="group relative bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      onClick={() => onViewDetails(product)}
    >
      {/* Badges */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        {product.isNew && (
          <span className="bg-[#7B1E2B] text-white text-xs font-semibold px-3 py-1 rounded-full">
            NEW
          </span>
        )}
        {product.isBestSeller && (
          <span className="bg-[#E6D7B3] text-[#001B2A] text-xs font-semibold px-3 py-1 rounded-full">
            BEST SELLER
          </span>
        )}
        {typeof product.stock === 'number' && product.stock <= 0 && (
          <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid={`sold-out-badge-${product.id}`}>
            SOLD OUT
          </span>
        )}
        {typeof product.stock === 'number' && product.stock > 0 && product.stock <= 5 && (
          <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full animate-pulse" data-testid={`low-stock-badge-${product.id}`}>
            ONLY {product.stock} LEFT!
          </span>
        )}
      </div>

      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.image || 'https://images.unsplash.com/photo-1610599905000-a8093ff22a55'}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(product);
            }}
            className="p-3 bg-white rounded-full hover:bg-[#E6D7B3] transition-colors transform hover:scale-110"
          >
            <Eye className="h-5 w-5 text-[#001B2A]" />
          </button>
          <button
            onClick={handleAddToCart}
            data-testid={`add-to-cart-${product.id}`}
            className="p-3 bg-white rounded-full hover:bg-[#E6D7B3] transition-colors transform hover:scale-110"
          >
            <ShoppingBag className="h-5 w-5 text-[#001B2A]" />
          </button>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-[#001B2A] mb-2 group-hover:text-[#7B1E2B] transition-colors">
          {product.name}
        </h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {product.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xl sm:text-2xl font-bold text-[#001B2A]">
            ${product.price.toFixed(2)}
          </span>
          <span className="text-xs sm:text-sm text-gray-500">{product.material}</span>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
